/* ═══════════════════════════════════════════════════════════════════════
   Setting a chat up, with crAIg rather than through him.

   Two panels on one screen. On the left, the settings themselves — every
   list the pack has, as rows you tick. On the right, crAIg. They are two
   views of ONE case: the tenant ticks a box and crAIg knows; crAIg records
   something and the box ticks itself.

   ── Why this replaced asking crAIg to offer the lists ──────────────────
   The first attempt had crAIg volunteer a list when his question happened
   to be one — a tool he could call to put tick boxes under his own reply.
   He never called it. Given a choice between that and writing four
   suggestions himself, a model picks the thing it has always done, and no
   amount of telling it otherwise moved it.

   Putting the lists on screen permanently removes the decision. There is
   no trigger to get right, nothing to volunteer, and no turn where the
   options fail to appear — they are simply there, the whole time, and
   crAIg talks beside them.

   ── Ticking is silent, and free ────────────────────────────────────────
   A click saves straight away through PATCH /checklists: no model call, no
   message in the transcript, no cost. crAIg is brought in ONCE, after they
   stop clicking, for the whole burst — see PAUSE below. Charging two model
   calls per tick would have made clicking around the expensive way to use
   this, which is precisely backwards.

   ── The same engine, a different shell ─────────────────────────────────
   This calls the same `runTurn` over the same API as the customer intake.
   The turn loop is repeated here rather than shared because the two
   screens need almost none of the same furniture — no language picker, no
   file drawer, no declaration — but there is no second brain, and there
   must never be one.
   ═══════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  runTurn,
  type CaseView,
  type PackView,
  type TenantView,
} from "./api";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { Bubble, SelectRow } from "./components/chrome";
import { PublishDrawer } from "./components/PublishDrawer";
import { CraigFace } from "./components/CraigFace";
import { Wordmark } from "./components/Brand";
import { StreamedMd } from "./components/typography";

interface Turn {
  role: "client" | "craig";
  text: string;
}

/**
 * How long to wait after the last click before crAIg says anything.
 *
 * Long enough that working down a list of five is one interruption rather
 * than five, short enough that it still feels like he was watching. Every
 * firing is a real model call, so this number is also the bill.
 */
const PAUSE = 2000;

/**
 * How long after the last keystroke a text field is saved.
 *
 * Short enough that nobody loses a value by clicking away, long enough
 * that a trading name is one write instead of twenty.
 */
const SAVE_AFTER = 700;

/**
 * Where a template with no group of its own lands.
 *
 * Named rather than left blank so one that forgets to declare a group
 * still appears under a heading somebody will read, instead of vanishing
 * into an unlabelled block at the bottom.
 */
const OTHER = "Everything else";

/**
 * The headings, in the order they appear.
 *
 * Fixed here rather than derived from the templates, so registering a new
 * one cannot quietly reorder the picker under somebody who had learned
 * where their trade lives. A group with nothing in it is not rendered.
 */
const GROUP_ORDER = ["Trades", "Home and property", "Appointments", "Health", "Professional", OTHER];

export function Builder({ slug }: { slug: string }) {
  const [theme, setTheme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  const [tenant, setTenant] = useState<TenantView | null>(null);
  const [pack, setPack] = useState<PackView | null>(null);
  const [kase, setCase] = useState<CaseView | null>(null);
  const [thread, setThread] = useState<Turn[]>([]);
  const [streaming, setStreaming] = useState("");
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  /* The drawer.
     ──────────────────────────────────────────────────────────────────
     Docked beside the two panels on a wide screen and slid over the top
     of them on anything narrower, because at 1280px there is room for a
     third column and below it there is not. Open by default where it is
     docked: it is the thing they came to press, and hiding it behind a
     button on a screen with space for it just adds a click. */
  const [wide, setWide] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(true);
  /* Worked starting points. Fetched once, and empty is a perfectly good
     state — the picker simply doesn't render, and the conversation works
     out the pack from what they say, exactly as it always has. */
  const [templates, setTemplates] = useState<
    { id: string; label: string; blurb: string; group: string | null }[]
  >([]);

  const threadBox = useRef<HTMLDivElement>(null);
  /* The composer keeps the cursor.
     ──────────────────────────────────────────────────────────────────
     It is disabled while a turn runs, and a disabled input loses focus —
     so without putting it back, every second message needs a click on the
     box first. That is invisible in a test and maddening in use. */
  const composer = useRef<HTMLInputElement>(null);

  useEffect(() => {
    applyTheme(theme);
    applyAccent(undefined, theme);
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1280px)");
    const apply = () => {
      setWide(mq.matches);
      setDrawerOpen(mq.matches);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  /* An overlaid drawer takes the scroll with it. Without this the page
     behind keeps scrolling under a finger meant for the drawer, which on a
     phone reads as the drawer being broken. */
  useEffect(() => {
    document.body.style.overflow = drawerOpen && !wide ? "hidden" : "";
    return () => void (document.body.style.overflow = "");
  }, [drawerOpen, wide]);

  /* ── One turn ─────────────────────────────────────────────────────────
     `panelChanged` names the lists they have been editing. Ids only: the
     server composes what crAIg actually reads, from the pack and the case,
     because a browser that can write into a system prompt is a browser
     writing crAIg's instructions. */
  const send = useCallback(
    async (text: string, caseId: string, panelChanged?: string[]) => {
      setBusy(true);
      setStreaming("");
      let acc = "";
      await runTurn(
        caseId,
        text,
        {
          onDelta(chunk) {
            acc += chunk;
            setStreaming(acc);
          },
          onDone(result) {
            setThread((t) => [...t, { role: "craig", text: result.say }]);
            setStreaming("");
            setCase(result.case);
          },
          onError(message) {
            setThread((t) => [...t, { role: "craig", text: message }]);
            setStreaming("");
          },
        },
        undefined,
        panelChanged,
      );
      setBusy(false);
      // After the re-enable, so the focus lands on an enabled field.
      requestAnimationFrame(() => composer.current?.focus());
    },
    [],
  );

  /* ── Boot ─────────────────────────────────────────────────────────────
     Straight into the conversation. The language question the customer
     intake opens with is not asked here: a tenant setting their own chat
     up is one person, and the screen they are looking at is already in
     the language they chose when they signed up. Their customers still
     each pick their own on arrival. */
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const boot = await api.tenant(slug);
        setTenant(boot.tenant);
        void api.options().then((o) => setTemplates(o.templates)).catch(() => {});
        const { case: created, pack: p } = await api.createCase(slug, "en");
        setCase(created);
        setPack(p);
        await send("", created.id);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [slug, send]);

  useEffect(() => {
    threadBox.current?.scrollTo({ top: threadBox.current.scrollHeight, behavior: "smooth" });
  }, [thread, streaming]);

  /* ── The panel, and the pause ─────────────────────────────────────────
     A tick writes immediately and optimistically: the row must respond
     under the finger, not after a round trip. The ids that came back from
     the server then replace the guess, so a value the pack rejected does
     not linger on screen looking accepted. */
  const pending = useRef<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Restart the clock. A burst of edits is one interruption, not five. */
  const nudgeAfterPause = useCallback(
    (caseId: string) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const touched = [...pending.current];
        pending.current.clear();
        if (touched.length && !busy) void send("", caseId, touched);
      }, PAUSE);
    },
    [busy, send],
  );

  /* ── Typing into a field ──────────────────────────────────────────────
     Two clocks, and they are different problems. The value is held locally
     and saved SAVE_AFTER milliseconds after the last keystroke, so a
     twenty-character trading name is one write rather than twenty. crAIg's
     own clock only starts once that write has landed — being asked about a
     business called "Coop" while somebody is halfway through typing
     "Coopers Plumbing" is worse than saying nothing. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const edit = useCallback(
    (name: string, value: string) => {
      if (!kase) return;
      setDrafts((d) => ({ ...d, [name]: value }));

      clearTimeout(saveTimers.current[name]);
      saveTimers.current[name] = setTimeout(() => {
        pending.current.add(name);
        void api
          .correctField(kase.id, { [name]: value })
          .then((r) => {
            setCase(r.case);
            // The server's value is now the truth; drop the local copy so
            // anything it normalised or rejected shows through.
            setDrafts((d) => {
              const { [name]: _gone, ...rest } = d;
              return rest;
            });
            nudgeAfterPause(kase.id);
          })
          .catch(() => setError("Couldn't save that just now."));
      }, SAVE_AFTER);
    },
    [kase, nudgeAfterPause],
  );

  const tick = useCallback(
    (listId: string, optionId: string) => {
      if (!kase || !pack) return;
      const list = pack.checklists.find((l) => l.id === listId);
      if (!list) return;

      const current = kase.checklists?.[listId] ?? [];
      let next: string[];
      if (optionId === list.noneId) {
        // "None of these" is exclusive both ways: ticking it clears the
        // rest, and ticking anything else clears it.
        next = current.includes(optionId) ? [] : [optionId];
      } else {
        const without = current.filter((x) => x !== list.noneId);
        next = without.includes(optionId)
          ? without.filter((x) => x !== optionId)
          : [...without, optionId];
      }

      setCase({ ...kase, checklists: { ...(kase.checklists ?? {}), [listId]: next } });
      pending.current.add(listId);

      void api
        .setChecklists(kase.id, { [listId]: next })
        .then((r) => setCase(r.case))
        .catch(() => setError("Couldn't save that just now."));

      nudgeAfterPause(kase.id);
    },
    [kase, pack, nudgeAfterPause],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      for (const t of Object.values(saveTimers.current)) clearTimeout(t);
    },
    [],
  );

  /* Recomputed by the server on every response that carries a case, so
     it moves with a tick and a keystroke as well as with a turn. Null only
     for something that is not a setup conversation, which /build never
     is — but typed honestly rather than asserted away. */
  const summary = kase?.summary ?? null;

  const chosenTemplate = kase?.fields?.template ?? "";
  /* Named `templateInfo` rather than `chosen`, because the checklist map
     below already binds `chosen` to the ticked option ids. */
  const templateInfo = templates.find((t) => t.id === chosenTemplate);

  /* Picking a starting point.
     ──────────────────────────────────────────────────────────────────
     Not debounced and not optimistic: this changes which pack the chat
     runs on, so the form below is about to be a different form, and
     guessing at it locally would flash the wrong questions. One round
     trip, then redraw from what the server says is true.

     crAIg gets told through the same pause as everything else, so
     picking a template and then ticking two things is still one turn. */
  const pickTemplate = useCallback(
    (id: string) => {
      if (!kase || busy) return;
      void api
        .chooseTemplate(kase.id, id)
        .then((r) => {
          setCase(r.case);
          setPack(r.pack);
          pending.current.add("template");
          nudgeAfterPause(kase.id);
        })
        .catch(() => setError("Couldn't set that just now."));
    },
    [kase, busy, nudgeAfterPause],
  );

  const say = () => {
    const trimmed = input.trim();
    if (!trimmed || !kase || busy) return;
    setThread((t) => [...t, { role: "client", text: trimmed }]);
    setInput("");
    void send(trimmed, kase.id);
  };

  if (error) return <Centre>{error}</Centre>;
  if (!pack || !kase) return <Centre>Getting things ready…</Centre>;

  return (
    <div className="min-h-screen flex flex-col">
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <CraigFace size={24} />
            <Wordmark size={15} />
            <span className="mono" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
              · setting up
            </span>
          </div>
          <div className="flex items-center gap-2">
          {/* The state of the whole setup, in one word, always on screen.

              A count rather than a bare "Publish": somebody four fields
              from finished should be able to see that from the top bar
              without opening anything, and somebody who is finished should
              not have to go looking for the button. */}
          {summary && !(wide && drawerOpen) && (
            <button
              type="button"
              className="btn btn-quiet btn-term"
              onClick={() => setDrawerOpen(true)}
              style={{ padding: "0.3rem 0.6rem" }}
            >
              {summary.blocking.length === 0
                ? summary.signed
                  ? "Publish"
                  : "Ready to sign"
                : `${summary.blocking.length} left`}
            </button>
          )}
          <button
            type="button"
            className="btn btn-quiet btn-term"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            aria-label="Switch theme"
            style={{ padding: "0.3rem 0.55rem" }}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
          </div>
        </div>
      </header>

      {/* Two panels wide; stacked on a phone with the settings first,
          because the thing you came to do is choose, and the conversation
          reads perfectly well underneath.

          The drawer makes it three at 1280px and up, which is also where
          it stops being an overlay — hence the wider container there, and
          `xl` rather than `lg`, so the two-panel screen keeps the room it
          had rather than being squeezed to make space for a drawer that
          is not docked yet. */}
      <main
        className={`flex-1 w-full mx-auto px-4 sm:px-5 py-5 grid gap-5 items-start lg:grid-cols-[1fr_1fr] ${
          wide && drawerOpen ? "max-w-[1500px] xl:grid-cols-[1fr_1fr_320px]" : "max-w-6xl"
        }`}
      >
        <section className="space-y-3">
          <div className="term-label">Your chat</div>
          <p style={{ fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.55, margin: 0 }}>
            Fill in whatever you like. crAIg is watching this and will pick up wherever you leave
            off — or ignore it entirely and just talk to him, and he'll fill it in for you.
          </p>

          {/* ── What kind of chat ──────────────────────────────────────
              The first question, and the one everything else hangs off.

              A dropdown rather than a row of tick boxes: exactly one
              answer is meant, and a column of buttons reads as "pick
              several". It is also the only control on screen until it is
              answered — a form that does not yet know what it is asking
              about can only ask everybody the same generic questions,
              which is the thing this replaced.

              Changing it later does not start anything over. What they
              have already told crAIg stays on the case; the questions
              around it change. */}
          {templates.length > 0 && (
            <div className="panel px-4 py-4">
              <label className="block">
                <span
                  className="term-label"
                  style={{ display: "block", marginBottom: 4, textTransform: "none" }}
                >
                  What kind of chat is this?
                </span>
                <select
                  className="field"
                  value={chosenTemplate}
                  disabled={busy}
                  onChange={(e) => pickTemplate(e.target.value)}
                >
                  <option value="">Choose one…</option>
                  {/*
                    Grouped, because a flat list of fourteen is a list
                    nobody reads to the end — and the business whose trade
                    is at the bottom of it concludes we do not do their
                    trade. GROUP_ORDER fixes the headings rather than
                    letting registration order decide, so adding a
                    template cannot silently reshuffle the picker.
                  */}
                  {GROUP_ORDER.filter((g) => templates.some((t) => (t.group ?? OTHER) === g)).map(
                    (g) => (
                      <optgroup key={g} label={g}>
                        {templates
                          .filter((t) => (t.group ?? OTHER) === g)
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                      </optgroup>
                    ),
                  )}
                </select>
              </label>
              <p style={{ fontSize: 12.5, color: "var(--ink-3)", margin: "8px 0 0", lineHeight: 1.45 }}>
                {templateInfo?.blurb ??
                  "None of these quite right? Leave it and just talk to crAIg — he'll work out what you need from what you tell him."}
              </p>
            </div>
          )}

          {/* Nothing below here until that is settled. The questions
              depend on it, and showing a generic form and then swapping it
              underneath somebody is worse than waiting a moment. */}
          {!chosenTemplate && templates.length > 0 && (
            <div className="panel px-4 py-4" style={{ fontSize: 13.5, color: "var(--ink-3)" }}>
              Pick one above and the rest of the form appears — the questions change depending on
              what kind of business this is.
            </div>
          )}

          {(Boolean(chosenTemplate) || templates.length === 0) && (
          <>
          {/* ── The details ────────────────────────────────────────────
              A real form, not a summary. Every field the pack asks for is
              editable here, and typing into one is exactly as good as
              telling him — both write the same case through the same
              validator.

              Nothing is marked as an error while it is empty. A form that
              turns red at somebody who has been on it for ten seconds is
              a form that reads as a test; crAIg raises what is missing in
              the conversation, which is the whole point of him. */}
          {/* ── One form ───────────────────────────────────────────────
              Typed answers and tick-list answers in a single panel, in the
              pack's own order, because to the person filling it in they
              are the same job. They used to be four stacked boxes — "the
              details", then three more with their own headings — which
              read as four forms and made the tick lists look like an
              afterthought below the real one. They are not: what crAIg
              should be able to answer is as much a setup question as the
              trading name. */}
          <div className="panel px-4 py-4 space-y-4">
            {pack.fields
              // The template has its own picker above, and as a text box
              // it would be a box asking somebody to type one of our ids.
              // Read off the pack rather than named here, so the next
              // field like it needs no change on this screen.
              .filter((f) => !f.internal)
              .map((f) => {
              const value = drafts[f.name] ?? kase.fields?.[f.name] ?? "";
              return (
                <label key={f.name} className="block">
                  <span
                    className="term-label"
                    style={{ display: "block", marginBottom: 4, textTransform: "none" }}
                  >
                    {f.label}
                    {!f.required && (
                      <span style={{ color: "var(--ink-3)", fontWeight: 400 }}> · optional</span>
                    )}
                  </span>
                  {f.choices ? (
                    <select
                      className="field"
                      value={value}
                      onChange={(e) => edit(f.name, e.target.value)}
                    >
                      <option value="">—</option>
                      {f.choices.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="field"
                      type={
                        f.type === "email" || f.type === "tel" || f.type === "date"
                          ? f.type
                          : "text"
                      }
                      value={value}
                      onChange={(e) => edit(f.name, e.target.value)}
                    />
                  )}
                </label>
                );
              })}

            {pack.checklists
              // A template can hide every option in a list. A heading with
              // nothing under it is worse than no heading.
              .filter((list) => list.options.length > 0)
              .map((list) => {
              const chosen = kase.checklists?.[list.id] ?? [];
              return (
                <div key={list.id}>
                  {/* Not uppercased, unlike every other label on this
                      screen: these titles say "crAIg", and the whole name
                      is the lower-case c. term-label would render CRAIG. */}
                  <div
                    className="term-label"
                    style={{ marginBottom: 6, textTransform: "none" }}
                  >
                    {list.title}
                  </div>
                  <div role="group" aria-label={list.title} className="flex flex-col gap-1.5">
                    {list.options.map((o) => (
                      <SelectRow
                        key={o.id}
                        active={chosen.includes(o.id)}
                        hint={o.hint}
                        onClick={() => tick(list.id, o.id)}
                      >
                        {o.label}
                      </SelectRow>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          </>
          )}
        </section>

        {/* ── crAIg ──────────────────────────────────────────────────── */}
        <section
          className="panel flex flex-col"
          style={{ position: "sticky", top: 16, height: "calc(100vh - 130px)", minHeight: 420 }}
        >
          <div
            ref={threadBox}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ scrollBehavior: "smooth" }}
          >
            {thread.map((m, i) => (
              <Bubble key={i} from={m.role === "client" ? "you" : "craig"}>
                {m.role === "client" ? m.text : <StreamedMd text={m.text} />}
              </Bubble>
            ))}
            {streaming && (
              <Bubble from="craig" speaking>
                <StreamedMd text={streaming} streaming />
              </Bubble>
            )}
            {busy && !streaming && (
              <div className="flex items-center gap-2.5">
                <CraigFace size={26} thinking />
                <span style={{ fontSize: 13, color: "var(--ink-3)" }} role="status">
                  thinking…
                </span>
              </div>
            )}
          </div>

          <div
            className="px-4 py-3 flex gap-2"
            style={{ borderTop: "1px solid var(--line)", background: "var(--surface-2)" }}
          >
            <input
              ref={composer}
              autoFocus
              className="field"
              style={{ flex: 1 }}
              placeholder="Tell him anything, or ask…"
              value={input}
              disabled={busy}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && say()}
            />
            <button type="button" className="btn btn-primary btn-term" disabled={busy} onClick={say}>
              Send
            </button>
          </div>
        </section>

        {/* ── What they are about to publish ─────────────────────────
            Rendered inside the grid so the docked case is simply a third
            column, and positioned out of it by its own `fixed` when it is
            an overlay. One element either way: two would be two states to
            keep in step, and the one that was off screen would rot. */}
        {summary && (
          <PublishDrawer
            kase={kase}
            pack={pack}
            summary={summary}
            wide={wide}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSigned={setCase}
          />
        )}
      </main>

      {/* The scrim. Overlay only — a docked drawer is part of the page and
          nothing behind it needs shutting out. */}
      {drawerOpen && !wide && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setDrawerOpen(false)}
          style={{ background: "rgba(20,20,18,.4)" }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <div className="flex flex-col items-center gap-3">
        <CraigFace size={40} thinking />
        <span style={{ fontSize: 14, color: "var(--ink-3)" }}>{children}</span>
        <Wordmark size={12} muted />
      </div>
    </div>
  );
}
